import React, {
  useRef,
  useEffect,
  useCallback,
  useContext,
  useState,
} from 'react';
import styled from 'styled-components';
import Cookie from 'universal-cookie';
import dateFormat from 'dateformat';
import { SocketContext } from './SocketProvider';
import { ChatContext } from './ChatProvider';

import verifiedIcon from '../../svg/check-mark.svg';

const MAX_MESSAGES = 100;
const MESSAGE_MAX_LENGTH = 500;
const USERNAME_MAX_LENGTH = 16;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 1rem;
  max-height: calc(100vh - 2rem);
  background-color: ${(p) => p.theme.color.white10};
`;

const Log = styled.div`
  width: 100%;
  flex: 1;
  overflow: auto;
  font-size: 0.8rem;
  line-height: 1.1rem;

  scrollbar-color: #aaa #0000;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 0.5rem;
  }

  &::-webkit-scrollbar-track {
    opacity: 0;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #aaa;
  }
`;

const Row = styled.div`
  padding: 0.3rem;
  font-size: 1rem;
  ${(p) => p.error && `color: ${(p) => p.theme.color.turquoise100}`};
  display: flex;
  align-items: flex-start;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const SendButton = styled.a`
  padding: 0.5rem;
  margin: 0.5rem;
  cursor: pointer;
  background-color: ${(p) => p.theme.color.white100};
  color: ${(p) => p.theme.color.grey100};

  &:hover {
    text-decoration: none;
    background-color: ${(p) => p.theme.color.boldText};
    color: ${(p) => p.theme.color.white100};
  }
`;

const Username = styled.input`
  padding: 0.5rem;
  margin: 0.5rem;
  flex: 1;
`;

const InputRow = styled.div`
  flex: 4;
  display: flex;
`;

const Prompt = styled.input`
  flex: 1;
  padding: 0.5rem;
  margin: 0.5rem;
`;

const VerifiedIcon = styled.img`
  width: 16px;
  margin: 0 0.3rem 0 0;
  filter: invert(1);
`;

const Timestamp = styled.div`
  white-space: nowrap;
  margin-right: 0.3rem;
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  font-weight: bold;
  opacity: 0.8;
`;

const RowText = styled.span`
  word-break: break-word;
`;

const RowUser = styled.span`
  white-space: pre;
  font-weight: bold;
  color: ${(p) => p.theme.color.boldText};

  &::after {
    content: ': ';
    color: ${(p) => p.theme.color.white100};
  }
`;

const Block = styled.span`
  display: block;
  word-break: break-all;
`;

const ErrorContainer = styled.div`
  height: 2rem;
  color: red;
`;

const Shoutbox = () => {
  const connection = useContext(SocketContext);
  const logContext = useContext(ChatContext);
  const cookieRef = useRef(null);
  const chatLogRef = useRef(null);
  const usernameRef = useRef(null);
  const msgRef = useRef(null);

  const [error, setError] = useState('');
  const [connectionReady, setConnectionReady] = useState(false);
  const errorTimeoutRef = useRef(null);

  const showError = (message) => {
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }
    setError(message);
    errorTimeoutRef.current = setTimeout(() => {
      setError('');
      errorTimeoutRef.current = null;
    }, 10000);
  };

  const showConnectionError = useCallback(
    (evt) => {
      logContext.setLog([
        {
          error: true,
          name: 'SERVER',
          text: 'Huutislaatikko ei saa yhteyttä! :(',
          timestamp: new Date(),
        },
      ]);
    },
    [logContext]
  );

  const handleData = useCallback(
    (evt) => {
      const data = JSON.parse(evt.data);
      let log = null;

      logContext.setLog((currentLog) => {
        log = currentLog || [];
        if (data.message && data.message.error) {
          showError(data.message.text);
        } else if (data.initial) {
          // Set the initial log
          log = data.initial;
        } else if (data.message) {
          // Check if the message already exists in the log
          const existingIndex = currentLog.findIndex(
            (m) => m._id === data.message._id
          );

          if (existingIndex !== -1) {
            // Replace the existing message with the updated one
            const updatedLog = [...currentLog];
            updatedLog[existingIndex] = {
              ...log[existingIndex],
              ...data.message,
            };
            log = updatedLog;
          } else {
            // Add the new message to the log
            const newLog = currentLog.concat([data.message]);
            if (newLog.length > MAX_MESSAGES) {
              newLog.splice(0, 1);
            }
            log = newLog;
          }
        } else if (data.erase) {
          // Remove the message from the log
          const id = data.erase;
          log = currentLog.filter((m) => m._id !== id);
        } else if (data.hide) {
          // Remove the message from the log
          const id = data.hide;
          log = currentLog.filter((m) => m._id !== id);
        } else if (data.type && data.type === 'connection_open') {
          setConnectionReady(true);
        }
        if (log) {
          log.sort((a, b) => {
            const dateA = new Date(Date.parse(a.timestamp));
            const dateB = new Date(Date.parse(b.timestamp));
            return dateA - dateB;
          });
        }
        return log;
      });
    },
    [logContext]
  );

  const sendMessage = () => {
    const text = msgRef.current.value,
      username = usernameRef.current.value;

    if (!username || username.length === 0) {
      alert('A username is required!');
      return;
    }

    if (!text || text.length === 0) {
      alert('A message is required!');
      return;
    }

    if (text.length > MESSAGE_MAX_LENGTH) {
      alert(`Message too long! Max: ${MESSAGE_MAX_LENGTH} characters.`);
      return;
    }

    if (username.length > USERNAME_MAX_LENGTH) {
      alert(`Username too long! Max: ${USERNAME_MAX_LENGTH} characters.`);
      return;
    }

    connection.send(
      JSON.stringify({
        name: username,
        text,
        type: 'message',
      })
    );

    msgRef.current.value = '';
  };

  const setUsername = () => {
    const username = usernameRef.current.value;
    cookieRef.current.set('username', username);
  };

  const renderRows = (rows) => {
    // Filter out messages from before 2026
    rows = rows.filter(
      (row) =>
        new Date(Date.parse(row.timestamp)) >= new Date('2026-04-01T00:00:00Z')
    );

    rows = rows.filter((row) => row.verified);

    return rows.map((row, index) => (
      <Row error={row.error} key={index}>
        <Block>
          <Timestamp>
            {dateFormat(new Date(Date.parse(row.timestamp)), 'dd.mm HH:MM')}
          </Timestamp>
        </Block>
        <Block>
          {(row.reserved || row.admin) && <VerifiedIcon src={verifiedIcon} />}
          <RowUser>{row.name}</RowUser>
          <RowText>{row.text}</RowText>
        </Block>
      </Row>
    ));
  };

  useEffect(() => {
    connection.addEventListener('message', handleData);
    connection.addEventListener('error', showConnectionError);

    return () => {
      connection.removeEventListener('message', handleData);
      connection.removeEventListener('error', showConnectionError);
    };
  }, [connection, handleData, showConnectionError]);

  const fetchMessages = () => {
    connection.send(
      JSON.stringify({
        type: 'fetch_messages',
        getUnverified: false,
      })
    );
  };
  // fetch all initial messages
  useEffect(() => {
    if (!connectionReady) return;

    if (connection.readyState === WebSocket.OPEN) {
      fetchMessages();
    } else {
      connection.onopen = () => {
        fetchMessages();
      };
      // safety fallback
      const timeout = setTimeout(() => {
        if (connection.readyState === WebSocket.OPEN) {
          fetchMessages();
        }
      }, 1000);

      return () => clearTimeout(timeout);
    }
  }, [connection, connectionReady]);

  // this is a bit cursed but this fetches the messages when the component is remounted :D
  useEffect(() => {
    if (connection.readyState === WebSocket.OPEN) {
      fetchMessages();
    }
  }, []);

  useEffect(() => {
    cookieRef.current = new Cookie();
    const username = cookieRef.current.get('username') || '';
    usernameRef.current.value = username;
  }, []);

  useEffect(() => {
    chatLogRef.current.scrollTop = chatLogRef.current.scrollHeight;
  });

  const onPromptKeyPress = (event) => {
    const ENTER_KEY = 13;

    if ([event.keyCode, event.which].includes(ENTER_KEY)) {
      sendMessage();
    }
  };

  return (
    <Container>
      <Log ref={chatLogRef}>{renderRows(logContext.log)}</Log>

      <InputContainer>
        <Username
          placeholder="Username"
          ref={usernameRef}
          maxLength={USERNAME_MAX_LENGTH}
          onChange={setUsername}
        />
        <InputRow>
          <Prompt
            placeholder="Huutista..."
            onKeyPress={onPromptKeyPress}
            ref={msgRef}
            maxLength={MESSAGE_MAX_LENGTH}
          />
          <SendButton onClick={sendMessage}>&nbsp;{'>'}&nbsp;</SendButton>
        </InputRow>
      </InputContainer>
      <ErrorContainer>{error}</ErrorContainer>
    </Container>
  );
};

export default Shoutbox;
